import { buildCategoryByOfferId, isOfferVisibleNow } from "@/lib/catalog-visibility";
import { Category, ItemOffer } from "@/types";
import { Atom, atom } from "jotai";
import availabilitiesAtom from "./availabilities";
import { minuteNowAtom, tenantTimezoneAtom } from "./tenant-clock";

/**
 * Categories to render right now, in catalog order.
 *
 * Derived from the offers record (which already dropped every category and
 * offer outside its scheduled window), so "shown in the nav" and "has a
 * section to scroll to" can never disagree: a category survives only when it
 * still has at least one offer on the menu at this hour. Categories whose
 * whole content is time-gated therefore disappear instead of leaving an empty
 * heading — and a nav chip pointing at nothing.
 */
export function createVisibleCategoriesAtom(
  categoriesAtom: Atom<Category[]>,
  categoriesItemOffersAtom: Atom<Record<string, ItemOffer[]>>,
) {
  return atom((get) => {
    const categories = get(categoriesAtom);
    const categoriesItemOffers = get(categoriesItemOffersAtom);

    return categories.filter((category) => (categoriesItemOffers[category.id]?.length ?? 0) > 0);
  });
}

/**
 * Flat list of offers that may surface outside a category section — the menu
 * search results.
 *
 * Same window rule as the sections, plus the owning category's window: a flat
 * list has no section to hide it, so an offer from a time-gated category would
 * otherwise leak back into search. An offer on no category of this menu (e.g.
 * only an option item, or a product of another channel's menu) is never listed.
 */
export function createVisibleItemOffersAtom(
  categoriesAtom: Atom<Category[]>,
  itemOffersAtom: Atom<ItemOffer[]>,
  hoursGateAtom: Atom<boolean> | null = null,
) {
  return atom((get) => {
    const categories = get(categoriesAtom);
    const itemOffers = get(itemOffersAtom);
    const availabilities = get(availabilitiesAtom);
        const options = {
      enforceHours: hoursGateAtom ? get(hoursGateAtom) : true,
      now: get(minuteNowAtom),
      timeZone: get(tenantTimezoneAtom),
    };

    const categoryByOfferId = buildCategoryByOfferId(categories);

    return itemOffers.filter((itemOffer) => {
      const category = categoryByOfferId.get(itemOffer.id);
      return category !== undefined && isOfferVisibleNow(itemOffer, availabilities, category, options);
    });
  });
}
